import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const adminDocumentAPI = createApi({
  reducerPath: 'AdminDocument',
  tagTypes: ['updateAdminList', 'documentInsights'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getDocumentList: builder.query({
      query: ({ applicationId, userId }) => {
        return {
          url: `b2b/documents/aggregate/loan`,
          method: 'GET',
          params: {
            ...(applicationId ? { applicationId } : {}),
            ...(userId ? { borrowerId: userId } : {}),
          },
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
       
        data: response?.data ?? [],
        total: Array.isArray(response?.data) ? response.data.length : 0,
        skip: 0,
        limit: Array.isArray(response?.data) ? response.data.length : 0,
      }),
      providesTags: ['updateAdminList'],
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          url: `b2b/documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        data: response?.data ?? [],
      }),
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `b2b/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const params = additionalParams || {};
        const normalizedParams = { ...params };

        if (
          normalizedParams.loan_application_id === undefined &&
          params.applicationId !== undefined
        ) {
          normalizedParams.loan_application_id = params.applicationId;
        }
        if (normalizedParams.borrower_id === undefined) {
          if (params.borrowerId !== undefined) {
            normalizedParams.borrower_id = params.borrowerId;
          } else if (params.userId !== undefined) {
            normalizedParams.borrower_id = params.userId;
          }
        }
        if (
          normalizedParams.document_category_id === undefined &&
          params.documentCategoryId !== undefined
        ) {
          normalizedParams.document_category_id = params.documentCategoryId;
        }
        if (normalizedParams.document_type_id === undefined) {
          if (params.documentTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentTypeId;
          } else if (params.documentSubTypeId !== undefined) {
            normalizedParams.document_type_id = params.documentSubTypeId;
          }
        }
        if (normalizedParams.title === undefined && params.documentTitle) {
          normalizedParams.title = params.documentTitle;
        }
        if (
          normalizedParams.document_type_code === undefined &&
          params.documentTypeCode !== undefined
        ) {
          normalizedParams.document_type_code = params.documentTypeCode;
        }

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('loan_application_id', normalizedParams.loan_application_id);
        appendIfDefined('borrower_id', normalizedParams.borrower_id);
        appendIfDefined('document_category_id', normalizedParams.document_category_id);
        appendIfDefined('document_type_id', normalizedParams.document_type_id);
        appendIfDefined('title', normalizedParams.title);

        Object.entries(normalizedParams).forEach(([key, value]) => {
          if (!appendedKeys.has(key)) {
            appendIfDefined(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        const { versionNotes } = additionalParams || {};
        if (versionNotes) {
          formData.append('version_notes', versionNotes);
        }
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/${documentId}/versions`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    verifyDocument: builder.mutation({
      query: ({ documentId, comment }) => {
        const headers = getHeaders();
        headers.Accept = 'application/json';
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/documents/${documentId}/approve`,
          method: 'POST',
          headers,
          body: comment ? { notes: comment } : undefined,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    rejectDocument: builder.mutation({
      query: ({ documentId, comment }) => {
        const headers = getHeaders();
        headers.Accept = 'application/json';
        return {
          url: `b2b/documents/${documentId}/reject`,
          method: 'POST',
          headers,
          body: comment ? { notes: comment } : undefined,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    uploadAndClassify: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        if (additionalParams?.loan_application_id)
          formData.append('loan_application_id', additionalParams.loan_application_id);
        if (additionalParams?.borrower_id)
          formData.append('borrower_id', additionalParams.borrower_id);
        if (additionalParams?.title)
          formData.append('title', additionalParams.title);
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: 'b2b/documents/upload-and-classify',
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateAdminList'],
    }),
    getDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `b2b/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    getDocumentSummaries: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/summaries',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
    getDocumentAccuracyMetrics: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/accuracy-metrics',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useUploadPredefinedDocsMutation,
  useUpdatePredefinedDocsMutation,
  useGetUserPropertyQuery,
  useDeleteDocumentMutation,
  useVerifyDocumentMutation,
  useRejectDocumentMutation,
  useUploadAndClassifyMutation,
  useLazyGetDocumentByIdQuery,
  useGetDocumentSummariesQuery,
  useGetDocumentAccuracyMetricsQuery,
} = adminDocumentAPI;
